import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import ItemCard from "../../components/ItemCard";
import StyledCheckbox from "../../components/StyledCheckbox";

import { api } from "../../services/api";
import {
  fireDeleteMsg,
  fireSuccessMsg,
  getDateString,
  onRequestError,
} from "../../services/functions";
import { useLoading } from "../../contexts/LoadingContext";

import fileDownload from "js-file-download";

const ListCarouselSlides = () => {
  const [carouselSlides, setCarouselSlides] = useState([]);
  const [selected, setSelected] = useState([]);
  const [csvFile, setCsvFile] = useState(null);

  const { setLoading } = useLoading();
  const location = useLocation();

  const locationRef = useRef();

  const routeName = "carousel-slides";

  const searchParams = new URLSearchParams(location.search);
  const pageFilter = searchParams.get("page");

  useEffect(() => {
    setLoading(true);
    getCarouselSlides();
    // eslint-disable-next-line
  }, [location.search]);

  const getCarouselSlides = async () => {
    let url = `/api/${routeName}`;
    if (pageFilter) {
      url += `?page=${pageFilter}`;
    }

    await api
      .get(url)
      .then(response => setCarouselSlides(response.data))
      .catch(e => {
        if (e.status === 404) {
          e.msg = "Não foi possível listar os slides!";
        }
        onRequestError(e.msg, e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!csvFile) return;

    const importCsv = async () => {
      api
        .post(
          "/api/import-carousel-slides",
          { file: csvFile },
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        .then(response => fireSuccessMsg(response.data.msg, getCarouselSlides))
        .catch(e => {
          if (!e.caught) {
            e.msg = "Não foi possível importar o arquivo .csv!";
          }
          onRequestError(e.msg, e);
        })
        .finally(() => {
          setLoading(false);
          setCsvFile(null);
          const el = document.getElementById("inp_csv_file");
          if (el) {
            el.value = null;
          }
        });
    };

    setLoading(true);
    importCsv();

    // eslint-disable-next-line
  }, [csvFile]);

  const getMassCarouselSlideNames = () => {
    let names = "";

    selected.forEach((item, i) => {
      names += item.id;
      if (i < selected.length - 1) {
        names += ", ";
      }
    });

    return names;
  };

  const onDeleteSuccess = (msg, deletedItems) => {
    fireSuccessMsg(msg);

    const copy = [...carouselSlides];
    deletedItems.forEach(item => {
      const index = copy.indexOf(item);
      copy.splice(index, 1);
    });
    setCarouselSlides(copy);
    setSelected([]);
    const inputs = document.querySelectorAll("input[id^=inp_item_]:checked");
    inputs.forEach(inp => (inp.checked = false));
  };

  const deleteSingleCarouselSlide = carouselSlide => {
    fireDeleteMsg(carouselSlide.id, async () => {
      setLoading(true);

      await api
        .delete(`/api/${routeName}/${carouselSlide.id}`)
        .then(response => onDeleteSuccess(response.data.msg, [carouselSlide]))
        .catch(e => onRequestError(e.msg, e))
        .finally(() => setLoading(false));
    });
  };

  const massDelete = () => {
    fireDeleteMsg(getMassCarouselSlideNames(), async () => {
      setLoading(true);

      await api
        .post(`/api/${routeName}/mass-delete`, {
          ids: selected.map(item => item.id),
        })
        .then(response => onDeleteSuccess(response.data.msg, selected))
        .catch(e => onRequestError(e.msg, e))
        .finally(() => setLoading(false));
    });
  };

  const onSelectAll = e => {
    const isChecked = e.target.checked;
    const inputs = document.querySelectorAll("[id^=inp_item_]");

    let allSelected = [];
    inputs.forEach((inp, i) => {
      inp.checked = isChecked;
      if (inp.checked) {
        allSelected = [...allSelected, carouselSlides[i]];
      }
    });
    setSelected(allSelected);
  };

  const onBlobReceived = blob => {
    const fileName = `slides-${getDateString()}.csv`;
    fileDownload(blob, fileName);
  };

  const exportCsv = async () => {
    setLoading(true);
    await api
      .get(`/api/export-carousel-slides`, { responseType: "blob" })
      .then(response => onBlobReceived(response.data))
      .catch(e => {
        if (!e.caught) {
          e.msg = "Não foi possível gerar o arquivo .csv!";
        }
        onRequestError(e.msg, e);
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="h-padded v-padded-lg">
      <h1>Slides {pageFilter === "/" ? "(Home)" : pageFilter === "/produtos" ? "(Produtos)" : ""}</h1>

      <div
        className="flexbox v-centered h-centered controls"
        style={{ margin: "3rem auto" }}
      >
        <Link 
          to={`/${routeName}/register${pageFilter ? `?defaultPage=${pageFilter}` : ""}`} 
          className="btn blue add"
        >
          <span className="material-symbols-rounded">add</span>
          <span className="btn-text">Cadastrar novo slide</span>
        </Link>

        <button
          className="btn blue btn-control"
          onClick={e => e.target.querySelector("input").click()}
        >
          <input
            id="inp_csv_file"
            type="file"
            hidden
            onChange={e => setCsvFile(e.target.files[0])}
            onClick={e => e.stopPropagation()}
          />
          <span className="material-symbols-rounded">upload_file</span>
          <span className="btn-text">Importar CSV</span>
        </button>

        {carouselSlides.length > 0 && (
          <button className="btn blue btn-control" onClick={exportCsv}>
            <span className="material-symbols-rounded">file_save</span>
            <span className="btn-text">Exportar CSV</span>
          </button>
        )}

        {selected.length > 0 && (
          <button className="btn red btn-control" onClick={massDelete}>
            <span className="material-symbols-rounded">delete</span>
            <span className="btn-text">Excluir selecionados</span>
          </button>
        )}
      </div>

      {carouselSlides.length > 0 && (
        <div className="flexbox" style={{ marginBottom: "1rem" }}>
          <StyledCheckbox
            id="inp_select_all"
            handler={onSelectAll}
            style={{ margin: "0 1rem" }}
          />
          <label htmlFor="inp_select_all" style={{ marginBottom: 0 }}>
            Selecionar todos
          </label>
        </div>
      )}

      <div>
        {carouselSlides.length === 0 && (
          <span
            style={{ display: "block", marginTop: "1rem", fontSize: "22px" }}
          >
            Nenhum slide encontrado
          </span>
        )}
        {carouselSlides.map((item, i) => (
          <ItemCard
            item={item}
            index={i}
            routeName={routeName}
            deleteHandler={deleteSingleCarouselSlide}
            selectionHandler={setSelected}
            selectionState={selected}
            warnings={[
              {
                label: "Sem imagem!",
                condition: !item.image_path,
              },
            ]}
            key={i}
          />
        ))}
      </div>
    </div>
  );
};

export default ListCarouselSlides;
